import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import {
  GAME_CATEGORIES,
  gameCategoryLabel,
  type CatalogueChangeReason,
  type CatalogueWriteResult,
  type GameCategory,
} from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { ReasonFields } from './ReasonFields.tsx';
import {
  catalogueRefusalTitle,
  DEFAULT_CREATE_REASON,
  noteIsValid,
  percentToBasisPoints,
} from './catalogue-write.ts';

/** The providers a new title may hang off, as the Providers tab already knows them. */
export interface ProviderChoice {
  id: string;
  code: string;
  name: string;
  status: string;
  driver: string;
}

/**
 * ADD GAME — `POST /api/games`.
 *
 * BORN DISABLED, AND ON NO BRAND'S SHELF. Both are stated before the click,
 * because both are surprises:
 *
 *   · Status. `POST /api/games/status` refuses LIVE for a game whose provider
 *     is not live, so a creation form offering LIVE would have to re-implement
 *     that gate or be able to produce a game the platform will not launch.
 *   · Brands. `game.game_brand` was seeded by a cross-join, so every title that
 *     already existed has a row per brand — and a title created here has none.
 *     It reaches no player until somebody uses the drawer's Brand availability
 *     control, which is the honest default for a title nobody has approved.
 *
 * THE DRIVER IS SHOWN BESIDE THE PROVIDER, as visible text. A title created on a
 * MOCK provider will list and refuse to launch, and an operator who was not told
 * that reads the refusal as a bug.
 */
export function AddGameDialog({
  open,
  providers,
  holdsKey,
  mfa,
  onClose,
  onCreated,
}: {
  open: boolean;
  providers: ProviderChoice[];
  holdsKey: boolean;
  mfa: boolean;
  onClose: () => void;
  onCreated: (result: CatalogueWriteResult) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [providerId, setProviderId] = useState('');
  const [category, setCategory] = useState<GameCategory>('SLOTS');
  const [subCategory, setSubCategory] = useState('');
  const [rtpPercent, setRtpPercent] = useState('');
  const [reasonCode, setReasonCode] = useState<CatalogueChangeReason>(DEFAULT_CREATE_REASON);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setName('');
    /* No provider is pre-selected. A pre-filled first-alphabetically provider is
       the field most likely to be submitted wrong, because it is the one an
       operator does not have to touch. */
    setProviderId('');
    setCategory('SLOTS');
    setSubCategory('');
    setRtpPercent('');
    setReasonCode(DEFAULT_CREATE_REASON);
    setNote('');
    setNoteTouched(false);
  }, [open]);

  const chosen = providers.find((p) => p.id === providerId) ?? null;
  const rtp = percentToBasisPoints(rtpPercent);
  const codeValid = /^[A-Z0-9_-]{2,48}$/.test(code);
  const ready =
    codeValid &&
    name.trim().length >= 2 &&
    providerId !== '' &&
    noteIsValid(note) &&
    rtp.kind !== 'invalid';

  const submit = useCallback(async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const result = await api.post<CatalogueWriteResult>('/api/games', {
        code,
        name: name.trim(),
        providerId,
        category,
        ...(subCategory.trim() === '' ? {} : { subCategory: subCategory.trim().toLowerCase() }),
        ...(rtp.kind === 'value' ? { declaredRtpBp: rtp.bp } : {}),
        reasonCode,
        note: note.trim(),
      });
      toast(
        `${result.name} was added, disabled and on no brand's shelf. Open it to set its brands, then set it live.`,
      );
      onCreated(result);
      onClose();
    } catch (error: unknown) {
      const raw = error instanceof ApiRequestError ? error.code : undefined;
      showError({
        title: catalogueRefusalTitle(isErrorCode(raw) ? raw : undefined, name.trim() || code, {
          allowed: holdsKey,
          mfa,
        }),
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(raw ? { code: raw } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [ready, code, name, providerId, category, subCategory, rtp, reasonCode, note, holdsKey, mfa, onCreated, onClose, showError, toast]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a game"
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!ready || submitting}
            onClick={() => void submit()}
          >
            Add game
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            <strong>The title is created disabled and on no brand&rsquo;s shelf.</strong> Two more
            deliberate steps put it in front of a player: open it and set its brand availability,
            then set it live. Neither happens here, and a title whose provider is not live cannot be
            set live at all.
          </div>
        </div>

        <Field
          label="Provider"
          hint="Which integration this title runs on. It cannot be changed later — a title belongs to the provider that serves it."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
            >
              <option value="">Choose a provider…</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* VISIBLE TEXT, not a title attribute: what this provider's driver means
            for a title created on it. An operator who is not told that a MOCK
            provider refuses every launch reads the refusal as a bug. */}
        {chosen && (
          <div className="notice notice-quiet">
            <Icon name={chosen.driver === 'MOCK' ? 'plugs' : 'plug'} />
            <div>
              {chosen.driver === 'MOCK'
                ? `${chosen.name} runs on the mock driver, a stand-in with no real provider behind it, so this title will appear in the catalogue and refuse every launch. That is the driver, not this title.`
                : `${chosen.name} runs on the ${chosen.driver} driver. The title's code must be the one ${chosen.name} uses for it, or the launch will be refused by the provider.`}
              {chosen.status !== 'LIVE' &&
                ` ${chosen.name} is currently ${chosen.status.toLowerCase()}, so this title cannot be set live until the provider is.`}
            </div>
          </div>
        )}

        <Field
          label="Game code"
          error={
            code !== '' && !codeValid
              ? 'Capitals, digits, hyphens and underscores only, 2 to 48 characters.'
              : undefined
          }
          hint="The PROVIDER's identifier for this title, and the key their catalogue import matches on. Unique across every provider, and it cannot be changed later."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={code}
              maxLength={48}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
              placeholder="JILI-1042"
            />
          )}
        </Field>

        <Field label="Display name" hint="What a player reads on the tile.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={name}
              maxLength={160}
              onChange={(e) => setName(e.target.value)}
              placeholder="Golden Empire"
            />
          )}
        </Field>

        <Field label="Category" hint="The platform's own shelf, which the player portal filters on.">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={category}
              onChange={(e) => setCategory(e.target.value as GameCategory)}
            >
              {GAME_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {gameCategoryLabel(c)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Table type"
          hint="Optional, and only a live-casino floor has one — the provider's own word, such as bac or rot. It groups the tiles within a provider on the player's shelf."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={subCategory}
              maxLength={32}
              onChange={(e) => setSubCategory(e.target.value)}
              placeholder="bac"
            />
          )}
        </Field>

        <Field
          label="Declared RTP (%)"
          error={rtp.kind === 'invalid' ? rtp.why : undefined}
          hint="Optional. The figure on the certificate — the RTP deviation report measures actual return against it. Leave empty for “not recorded”."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="number"
              step="0.01"
              min="0"
              max="100"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={rtpPercent}
              onChange={(e) => setRtpPercent(e.target.value)}
              placeholder="96.5"
            />
          )}
        </Field>

        <ReasonFields
          reasonCode={reasonCode}
          onReasonCode={setReasonCode}
          note={note}
          onNote={setNote}
          noteTouched={noteTouched}
          onNoteBlur={() => setNoteTouched(true)}
          placeholder="Added from JILI's September catalogue drop; certificate on file."
        />
      </div>
    </Dialog>
  );
}
